import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  StatusBadge,
  DepartmentClearanceCard,
  WarningBanner,
  InfoRow,
  EmptyState,
} from "./shared/components";
import {
  getNoDuesStatus,
  getNoDuesDeptDetail,
  getNoDuesSettlementStatus,
  downloadNoDuesSettlementReport,
  downloadDocument,
  getEmployeeDetails,
  formatINR,
  NoDuesCase,
  NoDuesDeptChecklistItem,
  NoDuesSettlementStatus,
} from "./services/eSeparation.service";

const HOW_IT_WORKS = [
  "No-Dues is triggered automatically after your resignation is approved.",
  "All departments process their clearance tasks in parallel.",
  "Each department's nodal officer reviews and clears your dues.",
  "Finance validates all outstanding loans and recoveries.",
  "HR performs final review after all departments clear.",
  "Certificates and relieving letter are generated upon completion.",
];

const DOCUMENTS = [
  {
    doc_type: "no_dues_certificate",
    name: "No-Dues Certificate",
    description: "Clearance from all departments",
    icon: "ti-rosette",
  },
];

const NoDuesTracker: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<NoDuesCase | null>(null);
  const [loading, setLoading] = useState(true);

  // Department detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDept, setDetailDept] = useState<string>("");
  const [detailItems, setDetailItems] = useState<NoDuesDeptChecklistItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Live consolidated recovery-settlement snapshot.
  const [settlement, setSettlement] = useState<NoDuesSettlementStatus | null>(
    null
  );
  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    getNoDuesStatus().then(async (res) => {
      const nd = res.data || null;

      // getNoDuesStatus frequently returns a blank employee name (only emp_no is
      // reliably present). Back-fill the name from the employee profile so the
      // "Employee" row is never empty.
      if (nd && !nd.emp_name && nd.emp_no) {
        try {
          const prof = await getEmployeeDetails(String(nd.emp_no));
          if (prof.data?.emp_name) nd.emp_name = prof.data.emp_name;
        } catch {
          /* keep whatever we have */
        }
      }

      setData(nd);
      const ndReqId = nd?.nodues_request_id;
      if (ndReqId) {
        const settleRes = await getNoDuesSettlementStatus(ndReqId);
        setSettlement(settleRes.data || null);
      }
      setLoading(false);
    });
  }, []);

  const handleDownloadReport = async () => {
    const ndReqId = data?.nodues_request_id;
    if (!ndReqId) return;
    setDownloadingReport(true);
    const res = await downloadNoDuesSettlementReport(ndReqId);
    setDownloadingReport(false);
    if (!res.success)
      toast.error(res.message || "Failed to download settlement report.");
  };

  const openDeptDetail = async (deptName: string, dispatchId?: number) => {
    if (!dispatchId) return;
    setDetailDept(deptName);
    setDetailOpen(true);
    setDetailLoading(true);
    const res = await getNoDuesDeptDetail(dispatchId);
    setDetailItems(res.data?.checklist_items || []);
    setDetailLoading(false);
  };

  const notEnabled = !loading && (!data || data.status === "Not Enabled");

  const total = data?.total_departments || 0;
  const cleared = data?.cleared_departments || 0;
  const progress = total > 0 ? (cleared / total) * 100 : 0;
  const allCleared = total > 0 && cleared === total;
  const isCompleted = data?.status === "Completed" || data?.status === "CLEARED";
  const fs = data?.finance_summary;

  const handleDownload = async (docType: string, docName: string) => {
    const res = await downloadDocument(docType, data?.nd_case_id || "");
    if (res.success) toast.success(`${docName} downloaded.`);
    else toast.error(`Failed to download ${docName}.`);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="No-Dues Clearance Status"
          subtitle="Track department-wise clearance progress"
          onExit={() => navigate(-1)}
        />

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : notEnabled ? (
          <>
            <WarningBanner
              type="info"
              message="No-Dues Clearance is not enabled yet. It will be enabled once your resignation is approved by the Competent Authority (CA) (status CA_APPROVED)."
            />
            <EmptyState
              icon="ti-clipboard-off"
              title="No Active Case"
              message="No-Dues clearance will be initiated automatically after your resignation is approved by the Competent Authority (CA)."
            />
          </>
        ) : (
          <>
            {/* Summary Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                      Case ID
                    </div>
                    <div className="fw-semibold">{data?.nd_case_id || "—"}</div>
                  </div>
                  <StatusBadge status={data?.status || "Pending"} />
                </div>

                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted small">Department Clearance Progress</span>
                  <span className="fw-semibold small">
                    {cleared} of {total}
                  </span>
                </div>
                <div className="progress" style={{ height: 8, borderRadius: 4 }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${progress}%`,
                      background: allCleared ? "#049123" : "#185FA5",
                    }}
                  />
                </div>
                <div className="text-end text-muted mt-1" style={{ fontSize: 11 }}>
                  {Math.round(progress)}% Complete
                </div>

                <div className="mt-3">
                  <InfoRow label="Employee" value={data?.emp_name || "—"} />
                  <InfoRow label="Employee No." value={data?.emp_no || "—"} />
                  <InfoRow label="Case Type" value={data?.case_type || "—"} />
                </div>
              </div>
            </div>

            {/* Department Clearance */}
            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: 12 }}>
              Department Clearance
            </h6>
            {data && data.departments.length > 0 ? (
              data.departments.map((dept, i) => (
                <div
                  key={i}
                  onClick={() =>
                    openDeptDetail(dept.dept_name, dept.dispatch_id)
                  }
                  style={{ cursor: dept.dispatch_id ? "pointer" : "default" }}
                >
                  <DepartmentClearanceCard
                    deptName={dept.dept_name}
                    nodalOfficer={dept.nodal_officer}
                    status={dept.status}
                    remarks={dept.remarks}
                    dueItems={dept.due_items}
                    recoveryAmount={dept.recovery_amount}
                  />
                </div>
              ))
            ) : (
              <EmptyState
                icon="ti-clipboard-off"
                title="No Active Case"
                message="No-Dues clearance will be initiated automatically after your resignation/separation is approved."
              />
            )}

            {/* Consolidated Recovery Settlement */}
            {settlement?.settlement && (
              <>
                <h6
                  className="text-uppercase text-muted mb-3 mt-4"
                  style={{ fontSize: 12 }}
                >
                  Recovery Settlement
                </h6>
                <div
                  className="card border-0 shadow-sm mb-4"
                  style={{ borderRadius: 16 }}
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div className="flex-grow-1">
                        <div className="fw-semibold">
                          {settlement.settlement.department_name}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          Consolidated recovery settlement
                        </div>
                      </div>
                      <span
                        className="badge"
                        style={{
                          background:
                            settlement.settlement.status === "CLOSED"
                              ? "#E6F4EA"
                              : "#FFF4E5",
                          color:
                            settlement.settlement.status === "CLOSED"
                              ? "#1B7A3D"
                              : "#BA7517",
                        }}
                      >
                        {settlement.settlement.status === "CLOSED"
                          ? "Closed"
                          : "In Progress"}
                      </span>
                    </div>

                    <InfoRow
                      label="Total Due"
                      value={`₹ ${Number(
                        settlement.settlement.total_due
                      ).toLocaleString("en-IN")}`}
                    />
                    <InfoRow
                      label="Total Collected"
                      value={`₹ ${Number(
                        settlement.settlement.total_collected
                      ).toLocaleString("en-IN")}`}
                    />

                    <hr className="my-2" />

                    {settlement.items.map((it) => (
                      <div
                        key={it.id}
                        className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      >
                        <div className="flex-grow-1 me-2">
                          <div className="fw-semibold small">{it.item_name}</div>
                          <small className="text-muted">
                            {it.source_department_name} • ₹
                            {Number(it.amount_due).toLocaleString("en-IN")}
                          </small>
                        </div>
                        <span
                          className="badge"
                          style={{
                            background: it.collected ? "#E6F4EA" : "#FDECEC",
                            color: it.collected ? "#1B7A3D" : "#B3261E",
                          }}
                        >
                          {it.collected
                            ? `Collected ₹${Number(
                                it.collected_amount
                              ).toLocaleString("en-IN")}`
                            : it.acted_at
                            ? "Not Collected"
                            : "Pending"}
                        </span>
                      </div>
                    ))}

                    {settlement.settlement.status === "CLOSED" && (
                      <button
                        className="btn d-flex align-items-center justify-content-center gap-2 w-100 mt-3"
                        style={{
                          border: "1px solid #993C1D40",
                          background: "#993C1D08",
                          color: "#993C1D",
                          height: 44,
                          borderRadius: 12,
                        }}
                        onClick={handleDownloadReport}
                        disabled={downloadingReport}
                      >
                        {downloadingReport ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                          />
                        ) : (
                          <i className="ti ti-download" />
                        )}
                        Download Settlement Report
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Finance Summary */}
            <h6 className="text-uppercase text-muted mb-3 mt-4" style={{ fontSize: 12 }}>
              Finance Summary
            </h6>
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
              <div style={{ height: 4, background: "#993C1D" }} />
              <div className="card-body">
                <InfoRow label="Outstanding Loans" value={formatINR(fs?.outstanding_loans)} />
                <InfoRow label="Advances Pending" value={formatINR(fs?.advances_pending)} />
                <InfoRow label="Training Recoveries" value={formatINR(fs?.training_recoveries)} />
                <InfoRow label="Short Notice Amount" value={formatINR(fs?.short_notice_amount)} />
                <hr className="my-2" />
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Total Recoverable</span>
                  <span
                    className="fw-bold"
                    style={{
                      color: "#C0282C",
                      fontSize: 16,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatINR(fs?.total_recoverable)}
                  </span>
                </div>
              </div>
            </div>

            {/* Documents */}
            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: 12 }}>
              Documents
            </h6>
            {isCompleted ? (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
                <div className="card-body">
                  {DOCUMENTS.map((doc) => (
                    <div
                      key={doc.doc_type}
                      className="d-flex align-items-center gap-3 p-2 rounded"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDownload(doc.doc_type, doc.name)}
                    >
                      <span
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{ width: 36, height: 36, borderRadius: 10, background: "#E6F1FB" }}
                      >
                        <i className={`ti ${doc.icon}`} style={{ color: "#185FA5" }} />
                      </span>
                      <div className="flex-grow-1">
                        <div className="fw-medium">{doc.name}</div>
                        <small className="text-muted">{doc.description}</small>
                      </div>
                      <i className="ti ti-download" style={{ color: "#185FA5" }} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <WarningBanner
                type="info"
                message="Documents will be available for download after all departments complete their clearance and final approval is obtained."
              />
            )}

            {/* How it works */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
              <div className="card-body">
                <h6 className="fw-semibold mb-3">
                  <i className="ti ti-info-circle me-2" style={{ color: "#185FA5" }} />
                  How No-Dues Works
                </h6>
                {HOW_IT_WORKS.map((text, i) => (
                  <div key={i} className="d-flex gap-2 mb-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        background: "#E6F1FB",
                        color: "#185FA5",
                        fontSize: 11,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span className="small">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Department detail modal */}
      {detailOpen && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1080 }}
          onClick={() => setDetailOpen(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-semibold">
                  {detailDept || "Department Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setDetailOpen(false)}
                />
              </div>
              <div className="modal-body">
                {detailLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-secondary" role="status" />
                    <p className="text-muted mt-3 mb-0">Loading items...</p>
                  </div>
                ) : detailItems.length > 0 ? (
                  detailItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="d-flex justify-content-between align-items-start p-2 mb-2 rounded"
                      style={{ background: "rgba(0,0,0,0.02)" }}
                    >
                      <div className="flex-grow-1 me-2">
                        <div className="fw-semibold small">{item.item_name}</div>
                        {item.comments && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {item.comments}
                          </div>
                        )}
                      </div>
                      <span className="fw-semibold" style={{ color: "#C0282C" }}>
                        ₹ {Number(item.amount_due || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                  ))
                ) : (
                  <EmptyState
                    icon="ti-clipboard-off"
                    title="No items recorded"
                    message="No due items were recorded for this department."
                  />
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  onClick={() => setDetailOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoDuesTracker;
